import { useCallback, useEffect, useRef, useState } from "react";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import { useDispatch, useSelector } from "react-redux";
import { idleFunds } from "../../../../../redux/features/Settings/Statistics";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * IdleFund - A component that displays idle funds trend over time using a line graph.
 * Shows daily idle fund amounts with average calculation. Supports filtering by
 * time period, amount unit, and favorite account selection.
 *
 * @component
 * @returns {JSX.Element} Rendered idle funds graph component
 */
const IdleFund = () => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst - Primary line data (idle fund amounts)
   * @property {number[]} dataSecond - Secondary line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number} totalIdleFund - Total idle fund amount
   */
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [40, 60, 40, 70, 50, 40, 70],
    dataSecond: [0, 15, 0, 0, 0, 0, 0],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    totalIdleFund: 5600000,
  };

  const favouriteAccounts = toCamelCase(globalText?.FAVOURITE_ACC);

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  // Amount unit filter state (e.g., "", "thousand", "lakh", "crore")
  const [selectedAmount, setSelectedAmount] = useState("");

  // Selected favorite account for filtering
  const [selectedAccount, setSelectedAccount] = useState("");

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(true);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  // Raw report data from API (stored for re-calculation when unit changes)
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);

  const dispatch = useDispatch();

  // Select loading and error states from Redux
  const { idleFundsLoading, idleFundsError } = useSelector(
    (state) => state.settingStatistics,
  );

  /**
   * Transforms API report data into arrays suitable for line graph rendering.
   * Handles amount formatting based on selected unit and extracts date labels.
   *
   */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        // X-axis position index
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(
          selectedAmount
            ? formatTotalBalance(val.total_idle_funds, selectedAmount).amount
            : val.total_idle_funds,
        );
        acc.graphDataSecond.push(
          selectedAmount
            ? formatTotalBalance("0.0", selectedAmount).amount
            : "0.0",
        );

        // Extract date label - prefers 'day' property, falls back to 'date'
        if (Object.hasOwn(val, globalText?.DAY.toLowerCase())) {
          acc.xAxisLabels.push(val.day);
        } else {
          acc.xAxisLabels.push(val.date);
        }
        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  /**
   * Fetches idle funds data from API and updates component state.
   * Requires a selected account to proceed.
   */
  const fetchData = useCallback(async () => {
    // Guard: no account selected
    if (!selectedAccount) return;

    const response = await dispatch(
      idleFunds({ owner_acct_id: selectedAccount.value, period: selectedDays }),
    );

    const reportData = response?.payload?.report_data;

    if (response?.payload?.status && reportData) {
      // Store raw data for re-calculation when amount unit changes
      setRawReportData(reportData);

      // Transform report data for graph
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(reportData.idle_fund_data || [], selectedAmount);

      // Update graph data with transformed arrays and summary values
      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        average_idle_funds: reportData?.average_idle_funds,
        totalIdleFund: reportData.total_idle_fund,
      });
      // toast shows only once
      if (!toastShownRef.current && fetchPageEnabled) {
        CommonToast({
          text: globalText?.FETCHED_SUCCESSFULLY, //"Fetched successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        toastShownRef.current = true;
      }
      setFetchPageEnabled(false);
      setIsError(false);
    } else {
      // fallback to defaults
      setGraphData(DEFAULT_GRAPH_DATA);
      // Enable fetch page unless it's a "not found" error (no data exists)
      setFetchPageEnabled(
        response?.payload?.status !== errorcode?.errorNotFound,
      );
      toastShownRef.current = false;

      setIsError(true);
    }
  }, [dispatch, selectedAccount, selectedDays]);

  /**
   * Handler for manual "Fetch Data" button click.
   * Simply disables fetch page to trigger auto-fetch effect.
   */
  const handleFetchClick = () => {
    setFetchPageEnabled(false);
  };

  /**
   * Effect: Auto-fetch data when fetch page is disabled (manual click or initial load).
   * Re-runs when fetchPageEnabled, fetchData, or dispatch changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled) {
      fetchData();
    }
  }, [fetchPageEnabled, fetchData, dispatch]);

  /**
   * Effect: Recalculate graph data when only the amount unit changes.
   * Uses cached rawReportData to avoid re-fetching from API.
   * Re-runs when selectedAmount or rawReportData changes.
   */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(
        rawReportData.idle_fund_data || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        average_idle_funds: rawReportData?.average_idle_funds,
        totalIdleFund: rawReportData.total_idle_fund,
      });
    }
  }, [selectedAmount, rawReportData]);

  return (
    <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md">
      {/* Header with title, period filter, amount unit filter, and account filter */}
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: `${globalText?.AMOUNT.toLowerCase()}s`,
            setterState: setSelectedAmount,
          },
          {
            type: favouriteAccounts,
            setterState: setSelectedAccount,
          },
        ]}
        titleText={globalText?.IDLE_FUNDS}
      />
      <div className="flex flex-col relative">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={fetchPageEnabled}
          loading={idleFundsLoading}
          isError={isError}
          errorData={
            idleFundsError && idleFundsError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          handleFunction={handleFetchClick}
        />

        {/* Line Graph Container with horizontal scrolling for many data points */}
        <div className="w-width-full pb-padding-sm relative overflow-x-auto ">
          {/* Dynamic minimum width based on data points count for proper spacing */}
          <div
            style={{
              minWidth: `${Math.max((graphData.xAxisData?.length || GlobalConfiguration?.lengthCheckZero) * SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint)}px`,
            }}
          >
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--failed)"}
              graphDataSecondColor={"var(--text-secondary)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
              amountFormatForLegend={selectedAmount}
            />
          </div>
        </div>

        {/* Footer with Average Idle Funds summary */}
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qsmd:flex-row flex-col Qsmd:gap-gap-md gap-gap-sm py-padding-sm px-padding-md">
            <div className="flex gap-gap-md">
              <div className="text-text-secondary text-font-xxs self-center">
                Average idle funds :
              </div>
              <div className="text-negative-act text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData?.average_idle_funds,
                      selectedAmount === ""
                        ? globalText?.AMOUNT.toLowerCase() //amount
                        : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdleFund;
